import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://beat-fussion-backend.onrender.com/api';

interface AuthUser {
  name: string;
  role: 'viewer' | 'team';
  token: string;
}

interface AuthContextType {
  user: AuthUser | null;
  login: (userData: AuthUser, refreshToken?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);

  // 🔹 Load from localStorage on refresh
  useEffect(() => {
    const storedUser = localStorage.getItem('authUser');
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  // 🔹 Save to localStorage when user changes
  useEffect(() => {
    if (user) localStorage.setItem('authUser', JSON.stringify(user));
    else localStorage.removeItem('authUser');
  }, [user]);

  // 🔹 LOGIN — save user + tokens
  const login = (userData: AuthUser, refreshToken?: string) => {
    setUser(userData);
    localStorage.setItem('authUser', JSON.stringify(userData));
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('role', userData.role);
    localStorage.setItem('token', userData.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
  };

  // 🔹 LOGOUT — call backend endpoint, then clear storage
  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const role = localStorage.getItem('role');
      const endpoint =
        role === 'team' ? `${API_BASE_URL}/team/logout/` : `${API_BASE_URL}/viewers/logout/`;

      if (refreshToken) {
        await axios.post(
          endpoint,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
      }
    } catch (err) {
      console.warn('Logout API call failed:', err);
    } finally {
      // Clear all auth data
      localStorage.removeItem('authUser');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('role');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
